import { useState } from 'react'
import { Plus, Pencil, Trash2, Search, MapPin } from 'lucide-react'
import { useUsers, useDeleteUser } from '@/services/useUsers'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { UserFormModal } from '@/components/settings/UserFormModal'
import type { User } from '@/types/common.types'
import { UserSitesModal } from '@/components/settings/UserSitesModal'

export function UserManagementPage() {
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    const [userToEdit, setUserToEdit] = useState<User | null>(null)
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
    const [userForSites, setUserForSites] = useState<User | null>(null)

    const { data, isLoading } = useUsers({ page, search: search || undefined })
    const { mutate: deleteUser } = useDeleteUser()

    const handleDelete = (user: User) => {
        if (window.confirm(`Are you sure you want to delete user "${user.username}"? This action cannot be undone.`)) {
            deleteUser(user.id)
        }
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold">User Management</h1>
                    <p className="text-neutral-600">
                        Manage system users and their permissions
                    </p>
                </div>
                <Button onClick={() => setIsCreateModalOpen(true)} className="shrink-0">
                    <Plus className="mr-2 h-4 w-4" />
                    Add User
                </Button>
            </div>

            <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-neutral-500" />
                    <Input
                        placeholder="Search users..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9"
                    />
                </div>
            </div>

            <div className="border rounded-lg overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Username</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Phone</TableHead>
                            <TableHead>Role</TableHead>
                            <TableHead>Assigned Sites</TableHead>
                            <TableHead>Last Login</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={7} className="text-center py-8">
                                    Loading...
                                </TableCell>
                            </TableRow>
                        ) : data?.data.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} className="text-center py-8">
                                    No users found
                                </TableCell>
                            </TableRow>
                        ) : (
                            data?.data.map((user) => (
                                <TableRow key={user.id}>
                                    <TableCell className="font-medium">{user.username}</TableCell>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>{user.phone_number || '-'}</TableCell>
                                    <TableCell>
                                        <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700">
                                            {user.role}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        {['FIELD_STAFF', 'SUPERVISOR'].includes(user.role) ? (
                                            <Button variant="outline" size="sm" onClick={() => setUserForSites(user)}>
                                                <MapPin className="mr-1.5 h-4 w-4" />
                                                {user.supervised_sites?.length || 0} sites
                                            </Button>
                                        ) : <span className="text-neutral-400">—</span>}
                                    </TableCell>
                                    <TableCell>
                                        {user.last_login
                                            ? new Date(user.last_login).toLocaleDateString()
                                            : 'Never'}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => setUserToEdit(user)}
                                        >
                                            <Pencil className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleDelete(user)}
                                        >
                                            <Trash2 className="h-4 w-4 text-red-600" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination - support both Laravel default (top-level) and meta-based shapes */}
            {(() => {
                const res = data as (typeof data) & { last_page?: number; total?: number; current_page?: number }
                const lastPage = res?.meta?.last_page ?? res?.last_page
                const total = res?.meta?.total ?? res?.total
                const currentPage = res?.meta?.current_page ?? res?.current_page ?? page
                if (!data || lastPage == null || lastPage <= 1) return null
                return (
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
                        <p className="text-sm text-neutral-600">
                            Showing {data.data?.length ?? 0} of {total ?? 0} users
                        </p>
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage(page - 1)}
                                disabled={page === 1}
                            >
                                Previous
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage(page + 1)}
                                disabled={page === lastPage}
                            >
                                Next
                            </Button>
                        </div>
                    </div>
                )
            })()}

            {/* Create/Edit Modal */}
            <UserFormModal
                open={isCreateModalOpen || !!userToEdit}
                onClose={() => {
                    setIsCreateModalOpen(false)
                    setUserToEdit(null)
                }}
                user={userToEdit}
            />
            <UserSitesModal user={userForSites} open={!!userForSites} onClose={() => setUserForSites(null)} />
        </div>
    )
}
